/**
 * Account Data Standardizer
 * Centralizes account data format handling to prevent inconsistencies
 * across components and services.
 *
 * This utility establishes a single source of truth for:
 * - Account number extraction from various formats
 * - Account data normalization
 * - Consistent field access patterns
 */

/**
 * Standard account data format interface
 * @typedef {Object} StandardAccountData
 * @property {string} accountId - Standardized account identifier
 * @property {string} accountNumber - Account number (alias for accountId)
 * @property {string} [nickname] - Account display name/nickname
 * @property {string} [type] - Account type (cash, margin, ira, etc.)
 * @property {string} [status] - Account status (active, inactive, etc.)
 * @property {Date|string} [openedAt] - Account opening date
 * @property {boolean} [isDayTrader] - Day trader status
 * @property {Object} [balance] - Balance information
 * @property {Object} [raw] - Original raw data for debugging
 */

/**
 * Extract account ID from any account data format
 * Handles all known variations: account_number, accountNumber, account-number, etc.
 *
 * @param {any} accountData - Account data in any format
 * @returns {string|null} Standardized account ID or null if not found
 */
export const extractAccountId = (accountData) => {
  if (!accountData) return null;

  // Handle primitive values (strings/numbers)
  if (typeof accountData === 'string' || typeof accountData === 'number') {
    return String(accountData).trim() || null;
  }

  // Handle objects - check all known account ID field variations
  if (typeof accountData === 'object') {
    const candidates = [
      // Primary fields (most common)
      accountData.accountId,
      accountData.accountNumber,
      accountData.account_number,
      accountData['account-number'],

      // Secondary fields
      accountData.account_id,
      accountData['account-id'],
      accountData.id,
      accountData.number,

      // Nested access patterns
      accountData.account?.accountNumber,
      accountData.account?.account_number,
      accountData.account?.['account-number'],
      accountData.account?.id,

      // Legacy/alternate patterns
      accountData.selected,
      accountData.accountNum,
      accountData.acctNumber,
      accountData.acct_number,

      // Array handling (take first item)
      Array.isArray(accountData.accounts) && accountData.accounts[0]
        ? extractAccountId(accountData.accounts[0])
        : null
    ];

    // Return first non-null, non-empty value
    for (const candidate of candidates) {
      if (candidate !== null && candidate !== undefined && candidate !== '') {
        return String(candidate).trim();
      }
    }
  }

  return null;
};

/**
 * Extract account nickname/display name from account data
 *
 * @param {any} accountData - Account data in any format
 * @returns {string|null} Account nickname or null if not found
 */
export const extractAccountNickname = (accountData) => {
  if (!accountData || typeof accountData !== 'object') return null;

  const candidates = [
    accountData.nickname,
    accountData.accountNickname,
    accountData.account_nickname,
    accountData['account-nickname'],
    accountData.displayName,
    accountData.name,
    accountData.accountName,
    accountData.account_name,
    accountData.label,
    accountData.description
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
};

/**
 * Extract account type from account data
 *
 * @param {any} accountData - Account data in any format
 * @returns {string|null} Account type or null if not found
 */
export const extractAccountType = (accountData) => {
  if (!accountData || typeof accountData !== 'object') return null;

  const candidates = [
    accountData.type,
    accountData.accountType,
    accountData.account_type,
    accountData['account-type'],
    accountData['account-type-name'],
    accountData.marginOrCash,
    accountData.margin_or_cash,
    accountData['margin-or-cash'],
    accountData.category,
    accountData.classification
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim().toLowerCase();
    }
  }

  return null;
};

/**
 * Extract day trader status from account data
 *
 * @param {any} accountData - Account data in any format
 * @returns {boolean|null} Day trader status or null if not found
 */
export const extractDayTraderStatus = (accountData) => {
  if (!accountData || typeof accountData !== 'object') return null;

  const candidates = [
    accountData.isDayTrader,
    accountData.is_day_trader,
    accountData['is-day-trader'],
    accountData.dayTraderStatus,
    accountData.day_trader_status,
    accountData['day-trader-status'],
    accountData.dayTrader,
    accountData.day_trader,
    accountData.pdt, // Pattern Day Trader
    accountData.isPDT
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'boolean') {
      return candidate;
    }
    if (typeof candidate === 'string') {
      const lower = candidate.toLowerCase().trim();
      if (lower === 'true' || lower === 'enabled' || lower === 'yes' || lower === '1') {
        return true;
      }
      if (lower === 'false' || lower === 'disabled' || lower === 'no' || lower === '0') {
        return false;
      }
    }
    if (typeof candidate === 'number') {
      return candidate > 0;
    }
  }

  return null;
};

/**
 * Extract account opening date from account data
 *
 * @param {any} accountData - Account data in any format
 * @returns {Date|string|null} Opening date or null if not found
 */
export const extractOpenedAt = (accountData) => {
  if (!accountData || typeof accountData !== 'object') return null;

  const candidates = [
    accountData.openedAt,
    accountData.opened_at,
    accountData['opened-at'],
    accountData.openDate,
    accountData.open_date,
    accountData.createdAt,
    accountData.created_at,
    accountData.dateOpened,
    accountData.date_opened
  ];

  for (const candidate of candidates) {
    if (candidate) {
      // Try to parse as date
      try {
        const date = new Date(candidate);
        if (!isNaN(date.getTime())) {
          return date;
        }
      } catch (e) {
        // If date parsing fails, return original value
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate.trim();
        }
      }
    }
  }

  return null;
};

/**
 * Standardize account data object to consistent format
 * This is the main function that components should use
 *
 * @param {any} accountData - Account data in any format
 * @param {Object} [options] - Standardization options
 * @param {boolean} [options.preserveRaw=true] - Whether to preserve raw data
 * @param {string} [options.fallbackNickname] - Fallback nickname if none found
 * @returns {StandardAccountData|null} Standardized account data or null
 */
export const standardizeAccountData = (accountData, options = {}) => {
  const { preserveRaw = true, fallbackNickname } = options;

  if (!accountData) return null;

  const accountId = extractAccountId(accountData);
  if (!accountId) return null; // Must have account ID

  const standardized = {
    accountId,
    accountNumber: accountId, // Alias for backward compatibility
    nickname: extractAccountNickname(accountData) || fallbackNickname || accountId,
    type: extractAccountType(accountData),
    isDayTrader: extractDayTraderStatus(accountData),
    openedAt: extractOpenedAt(accountData)
  };

  // Only include non-null values to keep object clean
  Object.keys(standardized).forEach(key => {
    if (standardized[key] === null || standardized[key] === undefined) {
      delete standardized[key];
    }
  });

  // Preserve raw data for debugging if requested
  if (preserveRaw && typeof accountData === 'object') {
    standardized.raw = accountData;
  }

  return standardized;
};

/**
 * Standardize array of account data
 *
 * @param {any[]} accountDataArray - Array of account data in any format
 * @param {Object} [options] - Standardization options
 * @returns {StandardAccountData[]} Array of standardized account data
 */
export const standardizeAccountDataArray = (accountDataArray, options = {}) => {
  if (!Array.isArray(accountDataArray)) return [];

  return accountDataArray
    .map(accountData => standardizeAccountData(accountData, options))
    .filter(Boolean); // Remove null results
};

/**
 * Find account in array by account ID
 * Handles all known ID variations
 *
 * @param {any[]} accountArray - Array of account data
 * @param {string} targetAccountId - Account ID to find
 * @returns {any|null} Found account data or null
 */
export const findAccountById = (accountArray, targetAccountId) => {
  if (!Array.isArray(accountArray) || !targetAccountId) return null;

  const targetId = String(targetAccountId).trim();

  return accountArray.find(account => {
    const accountId = extractAccountId(account);
    return accountId === targetId;
  }) || null;
};

/**
 * Validate that account data contains required fields
 *
 * @param {any} accountData - Account data to validate
 * @param {string[]} [requiredFields=['accountId']] - Required field names
 * @returns {boolean} True if valid, false otherwise
 */
export const validateAccountData = (accountData, requiredFields = ['accountId']) => {
  if (!accountData || typeof accountData !== 'object') return false;

  const standardized = standardizeAccountData(accountData);
  if (!standardized) return false;

  return requiredFields.every(field => {
    const value = standardized[field];
    return value !== null && value !== undefined && value !== '';
  });
};

/**
 * Migrate legacy account data to new standardized format
 * Useful for updating existing Firestore documents
 *
 * @param {any} legacyAccountData - Legacy account data
 * @returns {StandardAccountData|null} Migrated account data
 */
export const migrateLegacyAccountData = (legacyAccountData) => {
  if (!legacyAccountData) return null;

  // Handle common legacy patterns
  const migrated = {};

  // Extract from various legacy structures
  if (legacyAccountData.selectedAccount) {
    Object.assign(migrated, legacyAccountData.selectedAccount);
  }
  if (legacyAccountData.accountInfo) {
    Object.assign(migrated, legacyAccountData.accountInfo);
  }
  if (legacyAccountData.metadata?.selectedAccount) {
    Object.assign(migrated, legacyAccountData.metadata.selectedAccount);
  }

  // Merge in root level properties
  Object.assign(migrated, legacyAccountData);

  return standardizeAccountData(migrated, { preserveRaw: true });
};

/**
 * Create account data accessor object with standardized methods
 * Useful for components that need consistent account data access
 *
 * @param {any} accountData - Account data in any format
 * @returns {Object} Account accessor with standardized methods
 */
export const createAccountAccessor = (accountData) => {
  const standardized = standardizeAccountData(accountData);

  return {
    // Properties
    get accountId() { return standardized?.accountId || null; },
    get accountNumber() { return standardized?.accountNumber || null; },
    get nickname() { return standardized?.nickname || null; },
    get type() { return standardized?.type || null; },
    get isDayTrader() { return standardized?.isDayTrader || null; },
    get openedAt() { return standardized?.openedAt || null; },
    get raw() { return standardized?.raw || null; },

    // Methods
    isValid() { return validateAccountData(accountData); },
    toStandardFormat() { return standardized; },
    hasAccountId() { return !!this.accountId; },
    getDisplayName() { return this.nickname || this.accountId || 'Unknown Account'; },

    // Check if this account matches another account ID
    matches(otherAccountId) {
      return this.accountId === String(otherAccountId || '').trim();
    }
  };
};

// Export all functions as default object for convenient importing
export default {
  extractAccountId,
  extractAccountNickname,
  extractAccountType,
  extractDayTraderStatus,
  extractOpenedAt,
  standardizeAccountData,
  standardizeAccountDataArray,
  findAccountById,
  validateAccountData,
  migrateLegacyAccountData,
  createAccountAccessor
};